import { FC, useMemo } from "react";
import { useGummiwormContext } from "@/context/GummiwormContext";
import {
  aggregateAssetBalances,
  assetIdKey,
  findMarketForPair,
  formatAssetLabel,
  formatAssetValue,
  getAssetDecimals,
  marketMidPrice,
} from "@/lib/marketUtils";
import type { AssetId, AssetValue, Market } from "@/lib/wsTypes";
import { TradingPaneState } from "./TradingPaneState";
import { ConnectWalletButton } from "@/components/WalletConnect/ConnectWalletButton";
import { Countdown } from "@/components/Countdown";
import { useDepositTxs } from "@/context/GummiwormContext/hooks/useDepositTxs";

export const AvailableBalance: FC = () => {
  const { ledgerView, myAccountId, accountsReady } = useGummiwormContext();

  const myAccount = ledgerView.account;

  const fallbackAssets = useMemo<AssetValue[]>(() => {
    if (myAccountId == null) return [];

    const absorbedAssets = Object.values(ledgerView?.deposits)
      .filter((deposit) => deposit.accountId === myAccountId && deposit.absorbedInBlock !== 0n && deposit.rejectedInBlock === 0n)
      .flatMap((deposit) => deposit.value.assets);

    return aggregateAssetBalances(absorbedAssets).map((balance) => ({
      asset: balance.asset,
      value: balance.value,
    }));
  }, [ledgerView?.deposits, myAccountId]);

  const balanceAssets = useMemo(
    () => (myAccount?.uncommittedValue.assets.length ? myAccount.uncommittedValue.assets : fallbackAssets),
    [fallbackAssets, myAccount],
  );

  // Deposits registered but not yet absorbed (or rejected): surface them so a
  // just-deposited user sees their funds are on the way instead of an empty pane.
  // The absorption ETA comes from this browser's own deposit history (the
  // deposits channel carries no timestamps), so a deposit made elsewhere shows
  // without a timer.
  const { depositTxs } = useDepositTxs();
  const pending = useMemo<{ label: string; eta: number | null } | null>(() => {
    if (myAccountId == null) return null;
    const pendingDeposits = Object.values(ledgerView?.deposits)
      .filter((deposit) => deposit.accountId === myAccountId && deposit.absorbedInBlock === 0n && deposit.rejectedInBlock === 0n);
    const aggregated = aggregateAssetBalances(pendingDeposits.flatMap((deposit) => deposit.value.assets))
      .filter((balance) => balance.value > 0n);
    if (aggregated.length === 0) return null;
    const label = aggregated
      .map((balance) => `${formatAssetValue(balance.asset, balance.value)} ${formatAssetLabel(balance.asset)}`)
      .join(" + ");
    // The latest ETA across the pending set: all of them are in by that time.
    let eta: number | null = null;
    for (const deposit of pendingDeposits) {
      const t = depositTxs.get(deposit.requestId.toString())?.estimatedAbsorptionTime;
      if (t != null && (eta == null || t > eta)) eta = t;
    }
    return { label, eta };
  }, [ledgerView?.deposits, myAccountId, depositTxs]);

  // Surface a refund only when the latest deposit is the rejected one, so it
  // doesn't silently vanish -- but an old refund doesn't haunt the empty state
  // after later successful activity.
  const rejected = useMemo(() => {
    if (myAccountId == null) return null;
    const mine = Object.values(ledgerView?.deposits)
      .filter((deposit) => deposit.accountId === myAccountId)
      .sort((a, b) => (a.requestId < b.requestId ? -1 : 1));
    const latest = mine.at(-1);
    return latest != null && latest.rejectedInBlock !== 0n ? latest : null;
  }, [ledgerView?.deposits, myAccountId]);

  const balances = useMemo(() => {
    return balanceAssets
      .filter((assetValue: AssetValue) => assetValue.value > 0n)
      .map((assetValue: AssetValue) => ({
        key: `${formatAssetLabel(assetValue.asset)}-${assetValue.value.toString()}`,
        label: formatAssetLabel(assetValue.asset),
        formatted: formatAssetValue(assetValue.asset, assetValue.value),
      }));
  }, [balanceAssets]);
  type BalanceRow = (typeof balances)[number];

  // Total wallet value in the numeraire (VAN), priced at each asset's current
  // market mid. The numeraire is the quote shared by the markets; assets with no
  // tradable market against it (e.g. ADA) are ignored, not valued at zero.
  const markets = useMemo<Market[]>(() => Object.values(ledgerView?.markets ?? {}) as Market[], [ledgerView?.markets]);
  const numeraire = useMemo<AssetId | null>(() => {
    const counts = new Map<string, { asset: AssetId; count: number }>();
    for (const m of markets) {
      const key = assetIdKey(m.tradingPair.quote);
      const existing = counts.get(key);
      if (existing) existing.count += 1;
      else counts.set(key, { asset: m.tradingPair.quote, count: 1 });
    }
    let best: { asset: AssetId; count: number } | null = null;
    for (const candidate of counts.values()) {
      if (!best || candidate.count > best.count) best = candidate;
    }
    return best?.asset ?? null;
  }, [markets]);

  const totalLabel = useMemo(() => {
    if (!numeraire) return null;
    const depths = ledgerView.depths ?? {};
    const numeraireKey = assetIdKey(numeraire);
    let total = 0;
    let pricedAny = false;
    for (const av of balanceAssets) {
      if (av.value <= 0n) continue;
      const human = Number(av.value) / 10 ** getAssetDecimals(av.asset);
      const key = assetIdKey(av.asset);
      if (key === numeraireKey) {
        total += human;
        pricedAny = true;
        continue;
      }
      const match = findMarketForPair(key, numeraireKey, markets);
      if (!match) continue; // no tradable market against the numeraire -- ignore
      const mid = marketMidPrice(match.market, depths, ledgerView.tickers);
      if (mid == null || mid <= 0) continue;
      // Non-inverted market is asset/numeraire (mid = numeraire per asset);
      // inverted is numeraire/asset (mid = asset per numeraire).
      total += match.inverted ? human / mid : human * mid;
      pricedAny = true;
    }
    if (!pricedAny) return null;
    const amount = total.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return `~${amount} ${formatAssetLabel(numeraire)}`;
  }, [balanceAssets, numeraire, markets, ledgerView.depths, ledgerView.tickers]);

  return (
    <section className="sr-panel p-5" aria-label="Available balance">
      <div className="flex items-baseline justify-between gap-2 mb-4">
        <h3 className="text-[11px] font-medium text-sr-neutral-300 uppercase tracking-wider">
          Available Balance
        </h3>
        {totalLabel && (
          <span className="text-[11px] font-medium text-sr-neutral-400 tabular-nums font-display">
            {totalLabel}
          </span>
        )}
      </div>
      {balances.length === 0 ? (
        myAccountId != null && pending != null ? (
          <TradingPaneState
            compact
            eyebrow="Balances"
            title="Deposit on its way"
            message={`${pending.label} is being absorbed into the exchange.`}
            action={
              <span className="inline-flex items-center gap-2 rounded-full border border-sr-border-subtle px-3 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-sr-pink-muted">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-sr-pink-muted" />
                Absorbing
                {pending.eta != null && (
                  <Countdown
                    target={pending.eta}
                    className="tabular-nums normal-case tracking-normal text-sr-lemon-muted"
                  />
                )}
              </span>
            }
          />
        ) : myAccountId != null && rejected != null ? (
          <TradingPaneState
            compact
            eyebrow="Balances"
            title="Deposit refunded"
            message={rejected.failureReason
              ? `The exchange rejected your last deposit: ${rejected.failureReason}. The funds were refunded to your wallet.`
              : "The exchange rejected your last deposit and refunded it to your wallet."}
          />
        ) : (
          <TradingPaneState
            compact
            eyebrow="Balances"
            title={myAccountId == null ? "Connect a wallet" : "No deposited balances"}
            message={myAccountId == null
              ? "Connect a wallet to view the assets available for trading."
              : "Deposit funds into the exchange to make balances available here."}
            action={myAccountId == null ? <ConnectWalletButton /> : null}
          />
        )
      ) : (
        <div className="flex flex-col gap-3">
          {balances.map((balance: BalanceRow) => (
            <div key={balance.key} className="flex items-center justify-between">
              <span className="text-sm text-white">{balance.label}</span>
              <span className="text-sm font-medium text-white tabular-nums font-display">
                {balance.formatted}
              </span>
            </div>
          ))}
          {pending != null && (
            <div className="flex items-center justify-between text-sr-neutral-400">
              <span className="flex items-center gap-2 text-sm">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-sr-pink-muted" />
                On its way
                {pending.eta != null && <Countdown target={pending.eta} />}
              </span>
              <span className="text-sm font-medium tabular-nums font-display">
                +{pending.label}
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
